<template>
  <VResponsive
    :max-width="$attrs['max-width']"
    :max-height="$attrs['max-height']"
    :min-width="$attrs['min-width']"
    :min-height="$attrs['min-height']"
  >
    <VImg
      v-bind="$attrs"
      :key="retryCounter"
      :src="imageSrc"
      :cover="noImage || $attrs.contain"
      :lazy-src="require('@/assets/images/file/image-placeholder.svg')"
      :class="imageClass"
      @error="onError"
      @contextmenu="onContextMenu"
    >
      <template
        v-if="enableLoad && $slots.loader"
        #placeholder
      >
        <slot name="loader" />
      </template>
      <slot name="content" />
    </VImg>

    <VMenu
      v-model="showMenu"
      :style="{ position: 'absolute', left: mouseX + 'px', top: mouseY + 'px' }"
      absolute
      :close-on-content-click="false"
    >
      <VList>
        <VListItem
          v-for="(menuItem, index) in imageMenuList"
          :key="index"
          @click="menuItem.menuFunction(imageSrc)"
        >
          <template #prepend>
            <VAvatar size="32">
              <VIcon>
                {{ menuItem.icon }}
              </VIcon>
            </VAvatar>
          </template>

          <VListItemTitle>
            {{ menuItem.label }}
          </VListItemTitle>
        </VListItem>
      </VList>
    </VMenu>
  </VResponsive>
</template>

<script>
import AppUtils from '@/utils/AppUtils'
// TODO: Retry Load Image

/**
 * This is a image wrapper that extends v-img functionality which includes:
 * - Image loading animation / loader
 * - Error / Empty (Placeholder) Image
 * - Debug Image
 * - Image right click menu list
 *
 * `slot#loader`: Import custom component / template as loader
 *
 * @author Low Yee Huat <yeehuat.low@orbitmy.com>
 * @since 2022/07/18
 */
export default {
  components: {
  },
  props: {
    /**
     * Image source
     * - If not set, will show placeholder image
     */
    src: {
      type: String,
      default: null
    },
    /**
     * Whether to show placeholder random image
     */
    debug: {
      type: Boolean,
      default: AppUtils.isDebugMode()
    },
    /**
     * Whether to show loading animation
     */
    enableLoad: {
      type: Boolean,
      default: true
    },
    /**
     * Custom image source when error
     */
    failedSrc: {
      type: String,
      default: null
    },
    /**
     * Custom image source when image is not set
     */
    emptySrc: {
      type: String,
      default: null
    },
    /**
     * `true`: Shows errorImg when error
     * `false`: Shows emptyImg when error
     */
    showErrorWhenFailed: {
      type: Boolean,
      default: true
    },
    /**
     * Optional props for v-image class
     */
    imageClass: {
      type: [String, Object],
      default: ''
    },
    /**
     * Array list of menu item when right click on image
     * - label {String}: Menu item title
     * - icon {String}: Menu item icon
     * - menuFunction {Function}: Function on menu item
     */
    menuList: {
      type: Array,
      default: () => []
    }
  },
  data: function () {
    return {
      imageSrc: null,
      retryCounter: 0,
      showedFailedSrc: false,
      showMenu: false,
      mouseX: 0,
      mouseY: 0,
      noImage: false,
      defaultSize: 100,
      defaultMenuList: [
        // TODO: Copy Image to Clipboard
        // {
        //   label: 'Copy Image',
        //   icon: 'mdi-content-copy',
        //   menuFunction: async (imageSrc) => {
        //     // copy image to clipboard
        //     const img = document.createElement('img')
        //     const response = await fetch(imageSrc)
        //     const blob = await response.blob()
        //     // convert blob to base64
        //     const reader = new FileReader()
        //     reader.readAsDataURL(blob)
        //     // convert base to png
        //     await navigator.clipboard.write([new ClipboardItem({
        //       [blob.type]: blob
        //     })]).then(() => {
        //       console.log('Copy Image')
        //     }).catch(err => {
        //       console.log(err)
        //     })
        //   }
        // },
        {
          label: this.$t('components.imageWrapper.openImageNewTab'),
          icon: 'mdi-tab',
          menuFunction: (imageSrc) => {
            window.open(imageSrc, '_blank')
          }
        },
        {
          label: this.$t('components.imageWrapper.copyImageLink'),
          icon: 'mdi-content-copy',
          menuFunction: (imageSrc) => {
            // Copy image link to clipboard
            const clipboard = navigator.clipboard
            clipboard.writeText(imageSrc)
          }
        }
      ]
    }
  },
  computed: {
    imageMenuList () {
      return this.menuList.length ? this.menuList : this.defaultMenuList
    }
  },
  watch: {
    src: {
      handler: function (val) {
        if (val) {
          this.imageSrc = val
          return
        }

        if (this.debug) {
          this.imageSrc = this.genDebugSrc()
          return
        }

        this.imageSrc = this.genEmptySrc()
        this.noImage = true
      },
      immediate: true
    }
  },
  methods: {
    async onError (e) {
      if (this.showErrorWhenFailed) {
        this.imageSrc = this.genErrorSrc()
      } else {
        this.imageSrc = this.genEmptySrc()
      }

      this.noImage = true
      this.$emit('error', e)
    },
    onContextMenu (e) {
      e.preventDefault()

      this.showMenu = false
      this.mouseX = e.clientX
      this.mouseY = e.clientY

      this.$nextTick(() => {
        this.showMenu = true
      })

      this.$emit('contextmenu', e)
    },
    genErrorSrc () {
      if (!this.showedFailedSrc) {
        this.showedFailedSrc = true
        return this.failedSrc
      } else {
        return require('@/assets/images/file/image-broken-variant.svg')
      }
    },
    genEmptySrc () {
      if (this.emptySrc) {
        return this.emptySrc
      } else {
        return require('@/assets/images/file/image-placeholder.svg')
      }
    },
    genDebugSrc () {
      let debugSrc = 'https://picsum.photos'

      let haveSize = false

      if (this.$attrs.width || this.$attrs['max-width'] || this.$attrs['min-width']) {
        debugSrc += '/' + (this.$attrs.width ?? (this.$attrs['max-width'] ?? this.$attrs['min-width']))
        haveSize = true
      }

      if (this.$attrs.height || this.$attrs['max-height'] || this.$attrs['min-height']) {
        debugSrc += '/' + (this.$attrs.height ?? (this.$attrs['max-height'] ?? this.$attrs['min-height']))
        haveSize = true
      }

      if (!haveSize) {
        debugSrc += '/300'
      }

      debugSrc += '?random=' + Math.random()

      return debugSrc
    }
  }
}
</script>

<style scoped>
</style>
